import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
﻿import { useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, ScrollView, Share, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ConfirmModal';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { deleteAccount } from '@/lib/account';
import { gatherMyData } from '@/lib/gdpr';
import { PRIVACY, TERMS } from '@/lib/legalContent';

export default function MyDataScreen() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function onExport() {
    if (!user) return;
    setBusy(true);
    try {
      const data = await gatherMyData(user.id);
      const json = JSON.stringify(data, null, 2);
      if (Platform.OS === 'web') {
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `heirapsia-data-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        await Share.share({ message: json });
      }
    } catch (e) {
      appAlert(t('dataScreen.errorTitle'), getErrorMessage(e, t('dataScreen.errorExportFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    setDeleting(true);
    try {
      await deleteAccount();
      await signOut();
    } catch (e) {
      appAlert(t('dataScreen.errorTitle'), getErrorMessage(e, t('dataScreen.errorDeleteFailed')));
      setDeleting(false);
      setConfirming(false);
    }
  }

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
    >
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('dataScreen.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('dataScreen.title')}</Text>
        <View style={styles.spacer} />
      </View>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>{t('dataScreen.exportTitle')}</Text>
        <Text style={styles.cardText}>
          {t('dataScreen.exportText')}
        </Text>
        <Button
          title={Platform.OS === 'web' ? t('dataScreen.action_downloadJson') : t('dataScreen.action_exportShare')}
          icon="cloud-download-outline"
          loading={busy}
          onPress={onExport}
        />
      </View>

      {[TERMS, PRIVACY].map((d) => (
        <View key={d.title} style={styles.legal}>
          <Text style={styles.legalTitle}>{d.title}</Text>
          <Text style={styles.updated}>{d.updated}</Text>
          {d.sections.map((s) => (
            <View key={s.heading} style={styles.section}>
              <Text style={styles.heading}>{s.heading}</Text>
              <Text style={styles.body}>{s.body}</Text>
            </View>
          ))}
        </View>
      ))}

      <Text style={styles.body}>
        {t('dataScreen.storageNotice')}{' '}
        <Text style={styles.deleteLink} onPress={() => setConfirming(true)}>
          {t('dataScreen.deleteLinkText')}
        </Text>
        .
      </Text>

      <ConfirmModal
        visible={confirming}
        title={t('dataScreen.deleteModalTitle')}
        message={t('dataScreen.deleteModalMessage')}
        bullets={[
          t('dataScreen.deleteBullet1'),
          t('dataScreen.deleteBullet2'),
          t('dataScreen.deleteBullet3'),
          t('dataScreen.deleteBullet4'),
        ]}
        confirmLabel={t('dataScreen.deleteConfirmLabel')}
        danger
        loading={deleting}
        onConfirm={onDelete}
        onCancel={() => setConfirming(false)}
      />
    </ScrollView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: { paddingHorizontal: spacing.lg, gap: spacing.lg },
    topBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600', width: 70 },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    spacer: { width: 70 },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    cardTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    cardText: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 20 },
    legal: { gap: spacing.md },
    legalTitle: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700' },
    updated: { color: colors.textMuted, fontSize: fontSize.sm },
    section: { gap: spacing.xs },
    heading: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    body: { color: colors.textMuted, fontSize: fontSize.md, lineHeight: 22 },
    // Σκόπιμα ΙΔΙΟ μέγεθος/χρώμα με το υπόλοιπο κείμενο — διακριτικός, με υπογράμμιση.
    deleteLink: { color: colors.textMuted, fontSize: fontSize.md, textDecorationLine: 'underline' },
  });
}
